import { AfterViewInit, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_SNACK_BAR_DATA, MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { UserService as NgxUserService } from '@go-modules/services/user/user.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import * as dayjs from 'dayjs';
import { BreadCrumbStateService } from '@go-modules/services/breadcrumb-state/breadcrumb-state.service';
import { Subject, takeUntil } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { ROUTE_NAMES } from '@go-modules/services/router/router.service';

export enum SurveyMode {
	RATING = 'RATING',
	FEEDBACK = 'FEEDBACK'
}

@Component({
	selector: 'customer-survey-snack-bar',
	templateUrl: 'customer-survey-snack-bar.component.html',
	styleUrls: ['./customer-survey-snack-bar.component.scss'],
	standalone: false
})
export class CustomerSurveySnackBarComponent implements OnInit, AfterViewInit, OnDestroy {
	public static readonly localStorageKey = 'customer-survey';
	@ViewChild('firstFocusableElement') public firstFocusableElement!: ElementRef;
	public form = new UntypedFormGroup({
		rating: new UntypedFormControl(null),
		feedback: new UntypedFormControl('')
	});
	public hoveredRating: number = null;
	public mode: SurveyMode;
	public SurveyModes = SurveyMode;
	public inviteField: string|null = null;
	private componentDestroyed$$ = new Subject();

	constructor (
		@Inject(MAT_SNACK_BAR_DATA) public data,
		private ngxUserService: NgxUserService,
		private snackbarRef: MatSnackBarRef<CustomerSurveySnackBarComponent>,
		private ngxGoToastService: NgxGoToastService,
		private snackbar: MatSnackBar,
		private ngZone: NgZone,
		private breadCrumbStateService: BreadCrumbStateService,
		@Inject(userServiceToken) private userService: UserService
	) {
		this.mode = this.data.mode;

		if (this.mode === SurveyMode.RATING) {
			this.form.get('rating').setValidators([Validators.required]);
		} else {
			this.form.get('feedback').setValidators([Validators.required]);
			this.inviteField = 'feedback';
		}
	}

	public static open (snackBar: MatSnackBar, mode = SurveyMode.RATING) {
		const panelClass = ['snack-bar-panel'];

		if (mode === SurveyMode.RATING) {
			panelClass.push('is-rating');
		}

		return snackBar.openFromComponent(
			CustomerSurveySnackBarComponent,
			{
				panelClass,
				horizontalPosition: 'end',
				data: {
					mode
				}
			}
		);
	}

	public static canShow (userId) {
		const newKey = localStorage.getItem(`${userId}-` + CustomerSurveySnackBarComponent.localStorageKey);

		// Always show if no cached customer survey
		if (!newKey) {
			return true;
		}

		const nextShowDate = dayjs(newKey);
		const currentDate = dayjs();
		return currentDate.isAfter(nextShowDate);
	}

	public ngOnInit () {
		this.breadCrumbStateService.navState$
			.pipe(
				takeUntil(this.componentDestroyed$$),
				startWith(undefined)
			).subscribe((navState) => {
				if (navState === ROUTE_NAMES.DASHBOARD_SESSION_VIEW) {
					// snooze for 24 hours
					this.close(true);
				}
			});
	}

	public ngOnDestroy (): void {
		this.componentDestroyed$$.next(true);
		this.componentDestroyed$$.complete();
	}

	public ngAfterViewInit (): void {
		this.snackbarRef.afterOpened().subscribe(() => {
			this.firstFocusableElement.nativeElement.focus();
		});
	}

	public cancel () {
		// prevent an occasional issue where it doesnt close immediately
		this.ngZone.run(() => {
			this.snackbarRef.dismiss();
		});
	}

	public close (snoozed?: boolean) {
		const nextShowDate = snoozed ? dayjs().add(24, 'hour').toString() : dayjs().add(90, 'day').toString();
		localStorage.setItem(`${this.userService.currentUser.user_id}-` + CustomerSurveySnackBarComponent.localStorageKey, nextShowDate);

		// prevent an occasional issue where it doesnt close immediately
		this.ngZone.run(() => {
			this.snackbarRef.dismiss();
		});
	}

	public submit () {
		if (this.form.valid) {
			this.ngZone.run(() => {
				this.snackbarRef.dismiss();
			});
			this.ngxUserService.sendFeedback(
				this.form.controls.rating.value,
				this.form.controls.feedback.value,
				this.inviteField
			).subscribe({
				next: () => {
					this.ngxGoToastService.createToast({
						type: GoToastStatusType.SUCCESS,
						message: 'customer-feedback-thanks'
					});

					if (this.mode === SurveyMode.RATING) {
						localStorage
							.setItem(`${this.userService.currentUser.user_id}-` + CustomerSurveySnackBarComponent.localStorageKey, dayjs().add(90, 'day').toString());
					}
				},
				error: () => {
					// re open
					CustomerSurveySnackBarComponent.open(this.snackbar, this.mode);
					this.ngxGoToastService.createToast({
						type: GoToastStatusType.ERROR,
						message: 'customer-feedback-error'
					});
				}
			});
		}
	}
}
